import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Gauge, ArrowRight, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import {
  discoverTenants,
  isPostLoginBootstrapPending,
  logAuthRedirectDebug,
  normalizeEmail,
  normalizePhone,
} from '@/services/authClient';
import {
  buildTenantDjangoDashboardUrl,
  buildTenantSpaDashboardUrl,
  resolvePostLoginRedirectUrl,
  rewriteUrlToTenantOrigin,
} from '@/lib/postLoginRedirect';
import { POST_LOGIN_TARGET } from '@/config/api';

function GoogleIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

const STEP_IDENTIFIER = 'identifier';
const STEP_PICK_TENANT = 'pick_tenant';
const STEP_PASSWORD = 'password';
const STEP_NO_MATCH = 'no_match';

export default function SignInPage() {
  const { t, lang, pathFor, toggleLang } = useLanguage();
  const { loginOnTenant, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const s = t.signin;

  const [identifierMode, setIdentifierMode] = useState('email');
  const [email, setEmail] = useState('');
  const [phoneAreaCode, setPhoneAreaCode] = useState('+90');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState(STEP_IDENTIFIER);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [discoverError, setDiscoverError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  const showGoogleSignin = false;
  const redirectPath = location.state?.from?.pathname || pathFor('/dashboard');
  const normalizedIdentifier = identifierMode === 'email'
    ? normalizeEmail(email)
    : normalizePhone(phoneNumber, phoneAreaCode);

  if (!isAuthLoading && isAuthenticated) {
    return <Navigate to={redirectPath} replace />;
  }

  const resetToIdentifier = () => {
    setStep(STEP_IDENTIFIER);
    setTenants([]);
    setSelectedTenant(null);
    setPassword('');
    setDiscoverError('');
    setLoginError('');
  };

  const switchIdentifierMode = (mode) => {
    setIdentifierMode(mode);
    setDiscoverError('');
    setLoginError('');
    setSelectedTenant(null);
    setTenants([]);
    setPassword('');
    setStep(STEP_IDENTIFIER);
  };

  const handleDiscover = async (e) => {
    e.preventDefault();
    setDiscoverError('');

    if (identifierMode === 'email') {
      if (!normalizedIdentifier || !normalizedIdentifier.includes('@')) {
        setDiscoverError(lang === 'EN' ? 'Enter a valid email address.' : 'Gecerli bir e-posta girin.');
        return;
      }
    } else {
      const digits = normalizedIdentifier.replace(/\D/g, '');
      if (!normalizedIdentifier || digits.length < 7) {
        setDiscoverError(lang === 'EN' ? 'Enter a valid phone number.' : 'Gecerli bir telefon numarasi girin.');
        return;
      }
    }

    setDiscovering(true);
    try {
      const list = await discoverTenants(
        identifierMode === 'email'
          ? { email }
          : { phone: phoneNumber, phoneAreaCode }
      );
      setTenants(Array.isArray(list) ? list : []);

      if (!list || list.length === 0) {
        setStep(STEP_NO_MATCH);
        return;
      }
      if (list.length === 1) {
        setSelectedTenant(list[0]);
        setStep(STEP_PASSWORD);
        return;
      }
      setSelectedTenant(null);
      setStep(STEP_PICK_TENANT);
    } catch (err) {
      setDiscoverError(err.message || (lang === 'EN' ? 'Could not look up your account.' : 'Hesap aranamadi.'));
    } finally {
      setDiscovering(false);
    }
  };

  const handleContinueFromPick = (e) => {
    e.preventDefault();
    if (!selectedTenant) {
      setDiscoverError(lang === 'EN' ? 'Select a workspace.' : 'Bir calisma alani secin.');
      return;
    }
    setDiscoverError('');
    setStep(STEP_PASSWORD);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!selectedTenant) {
      setLoginError(lang === 'EN' ? 'No workspace selected.' : 'Calisma alani secilmedi.');
      return;
    }
    if (!password) {
      setLoginError(lang === 'EN' ? 'Password is required.' : 'Sifre gerekli.');
      return;
    }

    setLoggingIn(true);
    try {
      const payload = await loginOnTenant(selectedTenant, normalizedIdentifier, password);
      if (isPostLoginBootstrapPending(payload)) {
        return;
      }
      const fallbackTarget =
        POST_LOGIN_TARGET === 'django'
          ? buildTenantDjangoDashboardUrl(selectedTenant, lang)
          : buildTenantSpaDashboardUrl(selectedTenant, lang);
      const target =
        POST_LOGIN_TARGET === 'django'
          ? fallbackTarget || payload?.redirect_url
          : fallbackTarget || payload?.redirect_url || selectedTenant?.post_login_redirect_url || selectedTenant?.web_app_base_url;
      logAuthRedirectDebug('post-login target resolution', {
        post_login_target: POST_LOGIN_TARGET,
        fallback_target: fallbackTarget || '',
        payload_redirect_url: payload?.redirect_url || '',
        selected_tenant_redirect_url: selectedTenant?.post_login_redirect_url || '',
        selected_tenant_web_app_base_url: selectedTenant?.web_app_base_url || '',
        chosen_target: target || '',
        tenant: {
          slug: selectedTenant?.slug || selectedTenant?.schema_name || '',
          primary_domain: selectedTenant?.primary_domain || '',
          api_base_url: selectedTenant?.api_base_url || '',
        },
      });
      if (target) {
        window.location.assign(resolvePostLoginRedirectUrl(rewriteUrlToTenantOrigin(target, selectedTenant)));
        return;
      }
      navigate(redirectPath, { replace: true });
    } catch (err) {
      setLoginError(err.message || (lang === 'EN' ? 'Sign in failed.' : 'Giris basarisiz.'));
    } finally {
      setLoggingIn(false);
    }
  };

  const noMatchCopy =
    identifierMode === 'email'
      ? (lang === 'EN'
          ? 'No account was found for this email. Check the address or contact your administrator.'
          : 'Bu e-posta icin hesap bulunamadi. Adresi kontrol edin veya yoneticinize basvurun.')
      : (lang === 'EN'
          ? 'No account was found for this phone number. Check the number or contact your administrator.'
          : 'Bu telefon numarasi icin hesap bulunamadi. Numarayi kontrol edin veya yoneticinize basvurun.');

  return (
    <div className="min-h-screen flex" data-testid="signin-page">
      <div className="hidden lg:flex lg:w-[52%] bg-[#0D0303] flex-col p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'repeating-linear-gradient(0deg,#fff 0,#fff 1px,transparent 1px,transparent 48px),repeating-linear-gradient(90deg,#fff 0,#fff 1px,transparent 1px,transparent 48px)' }} />
        <div className="absolute top-0 left-0 w-1 h-full bg-[#A11212]" />

        <Link to={pathFor('/')} className="flex items-center gap-3 relative z-10" data-testid="signin-logo">
          <div className="w-9 h-9 bg-[#A11212] flex items-center justify-center">
            <Gauge className="w-5 h-5 text-white" />
          </div>
          <span className="font-black text-xl text-white tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            CarniTrack
          </span>
        </Link>

        <div className="flex-1 flex flex-col justify-center relative z-10 max-w-sm">
          <div className="w-8 h-[3px] bg-[#A11212] mb-8" />
          <h2 className="text-4xl xl:text-5xl font-black text-white leading-tight tracking-tighter mb-5"
            style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t.hero.headline1}<br />
            <span className="text-[#D32F2F]">{t.hero.headline2}</span><br />
            {t.hero.headline3}
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed">{t.hero.subtext}</p>
        </div>

        <div className="relative z-10 flex gap-10 pt-6 border-t border-white/10">
          {[
            { num: '500+', label: lang === 'EN' ? 'Facilities' : 'Tesis' },
            { num: '2M+', label: lang === 'EN' ? 'Animals Tracked' : 'Takip Edilen Hayvan' },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="text-2xl font-black text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>{stat.num}</div>
              <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 bg-white flex flex-col items-center justify-center px-8 py-12 relative">
        <Link to={pathFor('/')} className="flex items-center gap-2 mb-10 lg:hidden" data-testid="signin-mobile-logo">
          <div className="w-8 h-8 bg-[#A11212] flex items-center justify-center">
            <Gauge className="w-4 h-4 text-white" />
          </div>
          <span className="font-black text-lg text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>CarniTrack</span>
        </Link>

        <div className="w-full max-w-[340px]">
          <h1 className="text-2xl font-black text-gray-900 tracking-tighter mb-1"
            style={{ fontFamily: 'Chivo, sans-serif' }}>
            {s.title}
          </h1>
          <p className="text-sm text-gray-500 mb-8">{s.subtitle}</p>

          <div className={showGoogleSignin ? '' : 'hidden'}>
            <button data-testid="google-signin-btn" type="button"
              onClick={() => {}}
              className="w-full flex items-center justify-center gap-3 border border-gray-200 hover:border-gray-400 bg-white h-11 text-sm font-medium text-gray-700 transition-colors mb-4">
              <GoogleIcon />
              {s.googleBtn}
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-gray-100" />
              <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">{s.orText}</span>
              <div className="flex-1 h-px bg-gray-100" />
            </div>
          </div>

          {step === STEP_IDENTIFIER && (
            <form onSubmit={handleDiscover} className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => switchIdentifierMode('email')}
                  className={`h-10 border text-xs font-bold uppercase tracking-wider transition-colors ${
                    identifierMode === 'email'
                      ? 'border-[#A11212] bg-red-50 text-[#A11212]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {s.email}
                </button>
                <button
                  type="button"
                  onClick={() => switchIdentifierMode('phone')}
                  className={`h-10 border text-xs font-bold uppercase tracking-wider transition-colors ${
                    identifierMode === 'phone'
                      ? 'border-[#A11212] bg-red-50 text-[#A11212]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {s.phone}
                </button>
              </div>

              {identifierMode === 'email' ? (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {s.email}
                  </label>
                  <input
                    data-testid="signin-email-input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(ev) => { setEmail(ev.target.value); setDiscoverError(''); }}
                    placeholder={s.emailPlaceholder}
                    className="w-full h-11 border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212] transition-all"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                    {s.phone}
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={phoneAreaCode}
                      onChange={(ev) => { setPhoneAreaCode(ev.target.value); setDiscoverError(''); }}
                      className="h-11 border border-gray-200 bg-gray-50 px-3 text-sm text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212] transition-all"
                    >
                      <option value="+90">+90</option>
                      <option value="+1">+1</option>
                    </select>
                    <input
                      data-testid="signin-phone-input"
                      type="tel"
                      autoComplete="tel-national"
                      value={phoneNumber}
                      onChange={(ev) => { setPhoneNumber(ev.target.value); setDiscoverError(''); }}
                      placeholder={s.phonePlaceholder}
                      className="flex-1 h-11 border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212] transition-all"
                    />
                  </div>
                </div>
              )}

              {discoverError && <p className="text-xs text-red-500">{discoverError}</p>}
              <button
                type="submit"
                data-testid="signin-discover-btn"
                disabled={discovering}
                className="w-full h-11 bg-[#A11212] hover:bg-[#8B0F0F] disabled:bg-[#A11212]/60 text-white font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
              >
                {discovering ? (lang === 'EN' ? 'Continuing...' : 'Devam ediliyor...') : (lang === 'EN' ? 'Continue' : 'Devam')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}

          {step === STEP_NO_MATCH && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 leading-relaxed">{noMatchCopy}</p>
              <button
                type="button"
                onClick={resetToIdentifier}
                className="w-full h-11 border border-gray-200 hover:border-gray-400 text-gray-800 font-semibold text-sm flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {lang === 'EN' ? 'Try another identifier' : 'Baska tanimlayici dene'}
              </button>
            </div>
          )}

          {step === STEP_PICK_TENANT && (
            <form onSubmit={handleContinueFromPick} className="space-y-4">
              <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-2">
                {lang === 'EN' ? 'Choose your workspace' : 'Calisma alaninizi secin'}
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {tenants.map((tenant, idx) => {
                  const key = tenant.schema_name || tenant.primary_domain || String(idx);
                  const label = tenant.name || tenant.primary_domain || key;
                  const id = `tenant-${idx}`;
                  return (
                    <label
                      key={key}
                      htmlFor={id}
                      className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                        selectedTenant === tenant ? 'border-[#A11212] bg-red-50/30' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        id={id}
                        type="radio"
                        name="tenant"
                        checked={selectedTenant === tenant}
                        onChange={() => { setSelectedTenant(tenant); setDiscoverError(''); }}
                        className="accent-[#A11212]"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{label}</div>
                        <div className="text-xs text-gray-500 truncate">{tenant.primary_domain || tenant.slug || key}</div>
                      </div>
                      {tenant.role && (
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-600">
                          {tenant.role}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {discoverError && <p className="text-xs text-red-500">{discoverError}</p>}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={resetToIdentifier}
                  className="flex-1 h-11 border border-gray-200 text-gray-700 text-xs font-bold uppercase tracking-wider"
                >
                  {lang === 'EN' ? 'Back' : 'Geri'}
                </button>
                <button
                  type="submit"
                  data-testid="signin-tenant-continue-btn"
                  className="flex-1 h-11 bg-[#A11212] hover:bg-[#8B0F0F] text-white font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2"
                >
                  {lang === 'EN' ? 'Continue' : 'Devam'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {step === STEP_PASSWORD && selectedTenant && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="text-xs text-gray-600 mb-2">
                <span className="font-semibold text-gray-900">{selectedTenant.name || selectedTenant.primary_domain || 'Workspace'}</span>
                <span className="text-gray-400"> · </span>
                <span>{normalizedIdentifier}</span>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{s.password}</label>
                  <a href="#" data-testid="forgot-password-link"
                    className="text-[10px] text-[#A11212] hover:underline font-medium">{s.forgotPassword}</a>
                </div>
                <input
                  data-testid="signin-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(ev) => { setPassword(ev.target.value); setLoginError(''); }}
                  placeholder={s.passwordPlaceholder}
                  className="w-full h-11 border border-gray-200 bg-gray-50 px-3 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#A11212] focus:border-[#A11212] transition-all"
                />
              </div>
              {loginError && <p className="text-xs text-red-500">{loginError}</p>}
              <button
                type="submit"
                data-testid="signin-submit-btn"
                disabled={loggingIn}
                className="w-full h-11 bg-[#A11212] hover:bg-[#8B0F0F] disabled:bg-[#A11212]/60 text-white font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors"
              >
                {loggingIn ? (lang === 'EN' ? 'Signing in...' : 'Giris yapiliyor...') : s.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
              {tenants.length > 1 && (
                <button
                  type="button"
                  onClick={() => { setStep(STEP_PICK_TENANT); setPassword(''); setLoginError(''); }}
                  className="w-full text-xs text-gray-500 hover:text-gray-800"
                >
                  {lang === 'EN' ? 'Change workspace' : 'Calisma alanini degistir'}
                </button>
              )}
              {tenants.length <= 1 && (
                <button
                  type="button"
                  onClick={resetToIdentifier}
                  className="w-full text-xs text-gray-500 hover:text-gray-800"
                >
                  {lang === 'EN' ? 'Use a different email or phone' : 'Baska e-posta veya telefon kullan'}
                </button>
              )}
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              {s.noAccount}{' '}
              <Link to={pathFor('/setup')} className="text-[#A11212] font-semibold hover:underline" data-testid="setup-link">
                {s.setupLink}
              </Link>
            </p>
          </div>
        </div>

        <button onClick={toggleLang} data-testid="lang-toggle"
          className="absolute bottom-6 right-6 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 px-3 py-1 transition-colors">
          {lang === 'EN' ? 'Türkçe' : 'English'}
        </button>
      </div>
    </div>
  );
}
